import { ChartConfig, ChartContainer } from "@/components/ui/chart";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useTheme } from "@/components/theme-provider";
import { Pie, PieChart, Tooltip, Cell } from "recharts";
import { useMemo } from "react";

type FundingPieDatum = {
  id?: number | string;
  name?: string;
  obtained?: number;
  pourcent?: number;
};

type ChartDatum = {
  type: string;
  value: number;
  pourcent: number;
  fill: string;
};

const FALLBACK_PALETTE = [
  "#1D6F5F",
  "#2D8C69",
  "#B86A38",
  "#A4473D",
  "#506FA3",
  "#8C6BB1",
  "#D19A2A",
  "#4B8F8C",
  "#C05C7A",
  "#6C7A2B",
  "#C47E5B",
  "#4B5563",
];

const formatCurrency = (value: number) =>
  value.toLocaleString("fr-FR", {
    maximumFractionDigits: 0,
  });

const hashString = (value: string) => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(index);
    hash |= 0;
  }
  return Math.abs(hash);
};

const normalizeHsl = (value: string) => value.replace(/,/g, " ").replace(/\s+/g, " ").trim();

const parseHslValue = (value: string) => {
  const normalized = normalizeHsl(value);
  const match = normalized.match(/^([\d.]+)\s+([\d.]+)%\s+([\d.]+)%$/);

  if (!match) {
    return null;
  }

  return {
    h: Number(match[1]),
    s: Number(match[2]),
    l: Number(match[3]),
  };
};

const toHslString = (color: { h: number; s: number; l: number }) =>
  `hsl(${color.h} ${color.s}% ${color.l}%)`;

const adjustLightness = (
  color: { h: number; s: number; l: number },
  step: number
) => ({
  ...color,
  l: Math.max(24, Math.min(78, color.l + step)),
});

const getThemePalette = () => {
  if (typeof window === "undefined") {
    return FALLBACK_PALETTE;
  }

  const styles = getComputedStyle(document.documentElement);
  const vars = [
    "--primary",
    "--secondary",
    "--accent",
    "--color-green",
    "--color-blue",
    "--color-orange",
    "--color-red",
    "--color-gray",
    "--chart-1",
    "--chart-2",
    "--chart-3",
    "--chart-4",
    "--chart-5",
  ];

  const parsed = vars
    .map((cssVar) => parseHslValue(styles.getPropertyValue(cssVar)))
    .filter((value): value is { h: number; s: number; l: number } => Boolean(value));

  if (parsed.length === 0) {
    return FALLBACK_PALETTE;
  }

  const extended = parsed.flatMap((color, index) => {
    const alternateStep = index % 2 === 0 ? 10 : -8;
    return [color, adjustLightness(color, alternateStep)];
  });

  return extended.map(toHslString);
};

const getFallbackColor = (item: FundingPieDatum, index: number, palette: string[]) => {
  const key = `${item?.id ?? ""}-${item?.name ?? index}`;
  return palette[hashString(key) % palette.length] ?? FALLBACK_PALETTE[0];
};

const TypePieChart = ({ data = [] }: { data?: FundingPieDatum[] }) => {
  const { theme } = useTheme();
  const themePalette = useMemo(() => getThemePalette(), [theme]);

  const chartData = useMemo<ChartDatum[]>(
    () =>
      (data ?? []).map((item, index) => ({
        type: item?.name?.trim() || "Non renseigne",
        value: Number(item?.obtained ?? 0),
        pourcent: Number(item?.pourcent ?? 0),
        fill: getFallbackColor(item, index, themePalette),
      })),
    [data, themePalette]
  );

  const chartConfig = useMemo<ChartConfig>(() => {
    const updatedConfig: ChartConfig = {
      value: {
        label: "valeur",
      },
    };

    chartData.forEach((item, index) => {
      updatedConfig[`slice-${index}`] = {
        label: item.type,
        color: item.fill,
      };
    });

    return updatedConfig;
  }, [chartData]);

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) {
      return null;
    }

    const item = payload[0]?.payload as ChartDatum | undefined;

    if (!item) {
      return null;
    }

    return (
      <div className="rounded-2xl border border-[#e7e2d7] bg-white/95 px-3 py-2 shadow-lg backdrop-blur">
        <p className="m-0 max-w-[220px] text-sm font-semibold text-[#213547]">
          {item.type}
        </p>
        <p className="m-0 text-xs text-[#6b7280]">
          {item.pourcent} % du total
        </p>
        <p className="m-0 text-sm font-bold text-[#1D6F5F]">
          {formatCurrency(item.value)} €
        </p>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:gap-5">
      <div className="flex justify-center lg:w-[200px] lg:min-w-[200px] lg:flex-none">
        <ChartContainer
          config={chartConfig}
          className="aspect-square h-[250px] w-[200px] max-w-[250px]"
        >
          <PieChart>
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="type"
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={70}
              paddingAngle={1}
              stroke="#ffffff"
              strokeWidth={1}
            >
              {chartData.map((entry, index) => (
                <Cell key={`slice-${entry.type}-${index}`} fill={entry.fill} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ChartContainer>
      </div>

      <div className="min-w-0 flex-1 overflow-hidden">
        <ScrollArea className="h-[250px] w-full max-w-full">
          <div className="flex flex-col gap-2 pr-2">
          {chartData.map((entry) => (
            <div
              key={entry.type}
              className="grid w-full max-w-full grid-cols-[14px_minmax(0,1fr)_92px] items-center gap-3 rounded-lg px-1 py-1.5"
              title={entry.type}
            >
              <span
                className="h-3.5 w-3.5 shrink-0 rounded-full"
                style={{ backgroundColor: entry.fill }}
              />
              <div className="min-w-0 overflow-hidden">
                <p
                  className="m-0 overflow-hidden break-words text-sm font-medium leading-5 text-[#24303f]"
                  style={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {entry.type}
                </p>
              </div>
              <div className="pr-2 text-right">
                <p className="m-0 whitespace-nowrap text-sm font-semibold tabular-nums text-[#1f2937]">
                  {formatCurrency(entry.value)} €
                </p>
              </div>
            </div>
          ))}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
};

export default TypePieChart;
